/**
 * @file api-wms-scan.ts
 * @description Общий резолвер скана склада `POST /admin/wms/scan/resolve`
 *   (phase19.22, W17-3, W14-9; docs/api/wms-bins.md «Скан ячейки»).
 *
 * Основа поля `BinScanField` и шторки «Что лежит на полке»: сервер по
 * справочнику склада понимает, что отсканировали — ячейку, полку, стеллаж,
 * фасовку с партией или текст. Контракт зеркалит `ScanResolveResult`
 * (`apps/api/src/wms/scan/wms-scan.service.ts`).
 *
 * Ошибки 422, которые разбирает UI: `bin_not_found`, `bin_level_empty`,
 * `scan_sku_not_found`, `lot_not_found`, `lot_sku_mismatch`,
 * `scan_code_invalid`. Тексты сервер присылает человеческие.
 */

import { apiFetch } from "./api-client";
import type { LabelTemplate, LotLabelStatus } from "./api-labels";

/** Ячейка в ответе резолвера: адрес + «пусто или что лежит». */
export interface ResolvedBin {
  id: string;
  code: string;
  label: string | null;
  isActive: boolean;
  lotsCount: number;
  skuCount: number;
  skuCodes: string[];
  isEmpty: boolean;
}

export interface ResolvedSku {
  id: string;
  code: string;
  internalCode: string;
  displayName: string;
  productId: string;
  productName: string;
  gtin: string | null;
  isActive: boolean;
  labelTemplate: LabelTemplate | null;
}

export interface ResolvedLot {
  id: string;
  code: string;
  batchNumber: string;
  expiresAt: string | null;
  expiryRequired: boolean;
  labelStatus: LotLabelStatus;
  binCode: string | null;
}

export interface ResolvedStockRow {
  lotId: string | null;
  lotCode: string | null;
  batchNumber: string | null;
  expiresAt: string | null;
  labelStatus: LotLabelStatus | null;
  binId: string | null;
  binCode: string | null;
  quantity: string;
  reserved: string;
  available: string;
}

/** Полка стеллажа в ответе уровня `rack`. */
export interface ResolvedShelf {
  address: string;
  binsCount: number;
  lotsCount: number;
}

/** Уровень адреса: полка или стеллаж со списком активных ячеек. */
export interface ResolvedBinLevel {
  kind: "bin";
  level: "shelf" | "rack";
  address: string;
  bins: ResolvedBin[];
  /** Единственная активная ячейка уровня — подставить с показом адреса. */
  single: ResolvedBin | null;
  shelves?: ResolvedShelf[];
}

export type ScanResolveResult =
  | { kind: "bin"; level: "bin"; address: string; bin: ResolvedBin }
  | ResolvedBinLevel
  | {
      kind: "sku";
      source: "internal_code" | "label" | "lot" | "gtin";
      sku: ResolvedSku;
      lot: ResolvedLot | null;
      labelExpiresAt: string | null;
      stock: ResolvedStockRow[];
    }
  | { kind: "search"; source: "text" | "gtin"; query: string; items: ResolvedSku[] }
  | { kind: "unsupported"; scanKind: "box" | "order"; value: string };

export const wmsScanApi = {
  resolve: (input: { raw: string; warehouseId: string }) =>
    apiFetch<ScanResolveResult>("/admin/wms/scan/resolve", { method: "POST", json: input }),
};
